import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { DEFAULT_FILTERS, recipeKeys, toListQuery } from '../../pages/recipes/queries';
import { RecipePhoto } from '../recipes/RecipePhoto';
import { Alert } from '../Alert';

const PAGE_SIZE = 20;

/**
 * Search over the household's own recipes and the shared library, and pick one.
 * Used by "Add meal" on the week grid and "Add a recipe" in the plan editor.
 */
export function RecipePicker({
  onPick,
  busy = false,
  excludeIds = [],
  autoFocus = false,
}: {
  onPick: (recipe: { id: string; title: string; servings: number | null }) => void;
  busy?: boolean;
  /** Recipes already on the plan — shown as "on the plan" instead of pickable. */
  excludeIds?: readonly string[];
  autoFocus?: boolean;
}) {
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<'own' | 'public'>('own');
  const q = useDebouncedValue(search, 300);
  const filters = { ...DEFAULT_FILTERS, q, sort: 'title' as const };

  const list = useQuery({
    queryKey: recipeKeys.list(scope, filters),
    queryFn: () =>
      scope === 'public'
        ? api.recipes.listPublic(toListQuery(filters, { limit: PAGE_SIZE }))
        : api.recipes.list(toListQuery(filters, { limit: PAGE_SIZE })),
    placeholderData: (previous) => previous,
  });

  const recipes = list.data?.recipes ?? [];
  const tabClass = (isActive: boolean) =>
    `min-h-11 flex-1 rounded-lg px-3 text-sm font-semibold ${
      isActive ? 'bg-brand text-white' : 'border border-line bg-surface text-ink'
    }`;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <label htmlFor="plan-recipe-search" className="text-sm font-medium text-ink">
          Find a recipe
        </label>
        <input
          id="plan-recipe-search"
          type="search"
          value={search}
          autoFocus={autoFocus}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="chilli, sheet pan, garlic…"
          className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink placeholder:text-muted"
        />
      </div>

      <div role="group" aria-label="Recipe source" className="flex gap-2">
        <button
          type="button"
          aria-pressed={scope === 'own'}
          onClick={() => setScope('own')}
          className={tabClass(scope === 'own')}
        >
          Our recipes
        </button>
        <button
          type="button"
          aria-pressed={scope === 'public'}
          onClick={() => setScope('public')}
          className={tabClass(scope === 'public')}
        >
          Shared
        </button>
      </div>

      {list.isError ? <Alert tone="error">{messageForError(list.error)}</Alert> : null}

      <p className="sr-only" aria-live="polite">
        {list.isPending ? 'Searching recipes' : `${recipes.length} recipes found`}
      </p>

      {list.isPending ? <p className="text-sm text-muted">Loading recipes…</p> : null}
      {!list.isPending && recipes.length === 0 ? (
        <p className="text-sm text-muted">
          {scope === 'public'
            ? 'No shared recipes match that.'
            : 'No recipes match that. Try the shared library.'}
        </p>
      ) : null}

      <ul className="max-h-72 overflow-y-auto rounded-lg border border-line">
        {recipes.map((recipe) => {
          const already = excludeIds.includes(recipe.id);
          return (
            <li key={recipe.id} className="border-b border-line last:border-b-0">
              <button
                type="button"
                disabled={busy || already}
                onClick={() =>
                  onPick({ id: recipe.id, title: recipe.title, servings: recipe.servings })
                }
                className="flex w-full items-center gap-3 p-2 text-left hover:bg-brand-soft disabled:opacity-60 disabled:hover:bg-transparent"
              >
                <RecipePhoto
                  photoMediaId={recipe.photoMediaId}
                  title={recipe.title}
                  variant="thumb"
                  className="size-12 rounded-lg"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-ink">
                    {recipe.title}
                  </span>
                  <span className="block truncate text-xs text-muted">
                    {recipe.servings ? `serves ${recipe.servings}` : 'servings not recorded'}
                    {already ? ' · already on this meal' : ''}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
